import deburr from 'lodash-es/deburr';
import { distance } from 'fastest-levenshtein';

const RESULT_LIMIT = 7;
// Require four characters for a typo; allow two edits only on words of eight or more.
const MIN_FUZZY_WORD_LENGTH = 4;
const LONG_WORD_LENGTH = 8;
const MATCH_RANK = { exact: 0, normalized: 1, prefix: 2, words: 3, substring: 4, typo: 5, legalSuffix: 6 };
// Search-only fallback: legal wording varies between servicing entries for the same lender.
// Keep every other word, the original record, and its address; selection remains explicit.
const LEGAL_SUFFIX_WORDS = new Set(['company', 'llc', 'inc', 'corporation']);

// Numeric ordinals are spelling variants, not lender aliases. Match whole tokens only:
// `1st` becomes `first`, while `21st`, bare numbers, and names like `1stRate` stay intact.
const ORDINAL_WORDS = {
  '1st': 'first',
  '2nd': 'second',
  '3rd': 'third',
  '4th': 'fourth',
  '5th': 'fifth',
  '6th': 'sixth',
  '7th': 'seventh',
  '8th': 'eighth',
  '9th': 'ninth',
  '10th': 'tenth'
};

const normalize = (value) =>
  deburr(String(value ?? ''))
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\b(?:1st|2nd|3rd|[4-9]th|10th)\b/g, (ordinal) => ORDINAL_WORDS[ordinal])
    .trim();

function typoDistance(query, word) {
  // Short words and numbers are too ambiguous to correct. Never invent aliases.
  if (query.length < MIN_FUZZY_WORD_LENGTH || /\d/.test(query + word)) {
    return Infinity;
  }
  const edits = distance(query, word);
  if (edits <= (query.length >= LONG_WORD_LENGTH ? 2 : 1)) {
    return edits;
  }
  // Levenshtein counts a single adjacent transposition as two edits.
  if (edits === 2 && query.length === word.length) {
    const index = [...query].findIndex((letter, i) => letter !== word[i]);
    const swapped = query.slice(0, index) + query[index + 1] + query[index] + query.slice(index + 2);
    if (swapped === word) {
      return 1;
    }
  }
  return Infinity;
}

function matchWords(queryWords, words) {
  const remaining = [...words];
  // Reserve exact words first, then longer prefixes, so one word can't satisfy two query words.
  let unmatched = [...queryWords].sort((a, b) => b.length - a.length);
  for (const matches of [(a, b) => a === b, (a, b) => b.startsWith(a)]) {
    unmatched = unmatched.filter((query) => {
      const index = remaining.findIndex((word) => matches(query, word));
      if (index < 0) {
        return true;
      }
      remaining.splice(index, 1);
      return false;
    });
  }
  let edits = 0;
  for (const query of unmatched) {
    const distances = remaining.map((word) => typoDistance(query, word));
    const best = Math.min(...distances);
    if (!Number.isFinite(best)) {
      return Infinity;
    }
    edits += best;
    remaining.splice(distances.indexOf(best), 1);
  }
  return edits;
}

// Build once per directory update; keep the original options and servicing addresses intact.
export function createMortgageLenderSearchIndex(options) {
  return options.map((option, index) => {
    const text = String(option.text ?? option.value ?? '');
    const name = normalize(text);
    const words = name.split(' ');
    return {
      option,
      index,
      rawName: text.trim().toLowerCase(),
      name,
      words,
      legalSuffixWords: words.filter((word) => !LEGAL_SUFFIX_WORDS.has(word)),
      // Only a real directory id is searchable; AddMortgage falls back to the array index for
      // `option.id`, and matching that would rank an arbitrary lender above name matches.
      id: String(option.mortgage?.id ?? '').toLowerCase()
    };
  });
}

export function searchMortgageLenders(searchIndex, input) {
  const rawQuery = input.trim().toLowerCase();
  if (!rawQuery) {
    return searchIndex.slice(0, RESULT_LIMIT).map(({ option }) => option);
  }
  const query = normalize(input);
  const queryWords = query.split(' ');
  const legalSuffixQueryWords = queryWords.filter((word) => word && !LEGAL_SUFFIX_WORDS.has(word));

  return searchIndex
    .map(({ option, index, rawName, name, id, words, legalSuffixWords }) => {
      let rank = Infinity;
      let edits = 0;
      if (rawName === rawQuery || id === rawQuery) {
        rank = MATCH_RANK.exact;
      } else if (query && name === query) {
        rank = MATCH_RANK.normalized;
      } else if (query && name.startsWith(query)) {
        rank = MATCH_RANK.prefix;
      } else if (query) {
        edits = matchWords(queryWords, words);
        if (edits === 0) {
          rank = MATCH_RANK.words;
        } else if (name.includes(query) || id.includes(rawQuery)) {
          rank = MATCH_RANK.substring;
        } else if (Number.isFinite(edits)) {
          rank = MATCH_RANK.typo;
        }
        // Zero edits allows exact words and prefixes only: don't stack relaxed legal wording
        // with typo tolerance, or let a query containing only legal suffixes match everything.
        if (
          !Number.isFinite(rank) &&
          legalSuffixQueryWords.length &&
          matchWords(legalSuffixQueryWords, legalSuffixWords) === 0
        ) {
          rank = MATCH_RANK.legalSuffix;
        }
      }
      return { option, index, rank, edits };
    })
    .filter(({ rank }) => Number.isFinite(rank))
    .sort((a, b) => a.rank - b.rank || (a.rank === MATCH_RANK.typo ? a.edits - b.edits : 0) || a.index - b.index)
    .slice(0, RESULT_LIMIT)
    .map(({ option }) => option);
}
